import React, { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import EmployeeNavbar from "./EmployeeNavbar";
import FloatingAIChat from "../common/FloatingAIChat";

const EmployeeRoute = () => {
  const [loading, setLoading] = useState(true);
  const [isEmployee, setIsEmployee] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setIsEmployee(userData.isEmployee || false);
            setBusinessId(userData.businessId || null);

            // Get employee document ID from employees subcollection
            let employeeDocId = null;
            if (userData.businessId) {
              try {
                // Query employees collection to find doc with matching uid
                const employeesRef = collection(db, `businesses/${userData.businessId}/employees`);
                const employeeQuery = query(employeesRef, where("uid", "==", user.uid));
                const employeeSnap = await getDocs(employeeQuery);

                if (!employeeSnap.empty) {
                  employeeDocId = employeeSnap.docs[0].id;
                }
              } catch (empError) {
                console.warn("Could not fetch employee document ID:", empError);
              }
            }

            // Include both uid and employee document id
            setEmployeeData({
              ...userData,
              uid: user.uid,
              id: employeeDocId || user.uid, // Fallback to uid if no employee doc found
            });

            // Check if employee's business has an active subscription
            if (userData.businessId) {
              try {
                const businessDocRef = doc(db, "businesses", userData.businessId);
                const businessDocSnap = await getDoc(businessDocRef);

                if (businessDocSnap.exists()) {
                  const businessData = businessDocSnap.data();
                  const subscription = businessData.subscription;

                  // Check if subscription is active or in trial
                  const isActive =
                    subscription?.status === 'active' ||
                    subscription?.status === 'trialing';

                  setSubscriptionActive(isActive);

                  if (!isActive) {
                    setSubscriptionError('Your business subscription is inactive. Please contact your administrator.');
                  }
                } else {
                  setSubscriptionError('Business not found. Please contact your administrator.');
                }
              } catch (subError) {
                // If employee can't read business doc (permissions), assume active
                console.warn('Cannot verify subscription status:', subError);
                setSubscriptionActive(true);
              }
            }
          } else {
            console.error("🚨 User document not found in Firestore.");
          }
        } catch (error) {
          console.error("❌ Error fetching user document:", error);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  if (!isEmployee) {
    console.error("⛔ Not an Employee. Redirecting...");
    return <Navigate to="/unauthorized" />;
  }

  if (!businessId) {
    console.error("⛔ Business ID is missing. Redirecting...");
    return <Navigate to="/error" />;
  }

  // Check if subscription is active
  if (subscriptionError) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          {/* Warning Icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Subscription Inactive
          </h2>

          {/* Error Message */}
          <p className="text-gray-600 mb-6">
            {subscriptionError}
          </p>

          {/* Help Text */}
          <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
            <p className="mb-2">
              <strong>What does this mean?</strong>
            </p>
            <p>
              Your business account's subscription has expired or been cancelled.
              Please ask your business administrator to renew the subscription to regain access.
            </p>
          </div>

          {/* Logout Button */}
          <button
            onClick={() => auth.signOut()}
            className="mt-6 w-full px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (!subscriptionActive) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  // ✅ Pass businessId and employeeData to child components
  return (
    <>
      <EmployeeNavbar employeeData={employeeData} businessId={businessId} />
      <Outlet context={{ businessId, employeeData }} />
      {/* Floating AI Chat - available on all employee screens */}
      <FloatingAIChat businessId={businessId} />
    </>
  );
};

export default EmployeeRoute;
